'use client'

import { useMemo, type ReactNode } from 'react'
import {
  CalendarIcon,
  CodeIcon,
  ColorWheelIcon,
  Component1Icon,
  ComponentBooleanIcon,
  ComponentInstanceIcon,
  FileIcon,
  FileTextIcon,
  FrameIcon,
  InputIcon,
  Component2Icon,
  TextAlignJustifyIcon,
  TextIcon,
  LayersIcon,
  Link2Icon,
  DiscIcon,
  ComponentNoneIcon,
  ImageIcon,
  SpeakerLoudIcon,
  VideoIcon,
  DashboardIcon,
} from '@radix-ui/react-icons'
import { Block, isReferenceBlock } from '@basehub/data/replicache/index'
import { match } from 'ts-pattern'
import { AtLeast } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { UnionBlockIcon } from '../icons/union'
import { OgImageIcon } from '~/components/icons/og-image'
import { SVGIcon } from '../icons/svg'
import { WorkflowBlockIcon } from '../icons/workflow'
import { useBlockIconStore } from './store'
import { useQuery } from '@tanstack/react-query'
import { STARTIcon } from '../icons/START'

type BlockIconProps = {
  block: AtLeast<Block, 'type'>
  parent?: Block
  iconProps?: React.ComponentPropsWithRef<typeof Component1Icon>
  forceNonEmptyDocumentIcon?: boolean
  forceReferenceLinkIcon?: boolean
}

export const BlockIcon = ({
  block,
  parent,
  iconProps,
  forceNonEmptyDocumentIcon,
  forceReferenceLinkIcon,
}: BlockIconProps) => {
  if (block.id?.startsWith('invalid-reference')) {
    return <ComponentNoneIcon {...iconProps} />
  }
  return match(block)
    .with({ type: 'document' }, (b) => (
      <DocumentIcon
        forceNonEmptyDocumentIcon={forceNonEmptyDocumentIcon}
        iconProps={iconProps}
        block={b}
      />
    ))
    .with({ type: 'component' }, (b) => (
      <ReferenceableIcon
        parent={parent}
        block={b}
        forceReferenceLinkIcon={forceReferenceLinkIcon}
        iconProps={iconProps}
      >
        <Component1Icon {...iconProps} />
      </ReferenceableIcon>
    ))
    .with({ type: 'list' }, () => <Component2Icon {...iconProps} />)
    .with({ type: 'instance' }, (b) => (
      <ReferenceableIcon
        parent={parent}
        block={b}
        forceReferenceLinkIcon={forceReferenceLinkIcon}
        iconProps={iconProps}
      >
        <ComponentInstanceIcon {...iconProps} />
      </ReferenceableIcon>
    ))
    .with({ type: 'text' }, () => <TextIcon {...iconProps} />)
    .with({ type: 'number' }, () => <FrameIcon {...iconProps} />)
    .with({ type: 'boolean' }, () => <ComponentBooleanIcon {...iconProps} />)
    .with({ type: 'date' }, () => <CalendarIcon {...iconProps} />)
    .with({ type: 'media' }, (b) => {
      const allowedTypes = Object.keys(b.constraints?.allowedMimeTypes ?? {})
      if (allowedTypes.length === 1) {
        switch (allowedTypes[0]) {
          case 'image':
            return <ImageIcon {...iconProps} />
          case 'video':
            return <VideoIcon {...iconProps} />
          case 'audio':
            return <SpeakerLoudIcon {...iconProps} />
          default:
            return <FileIcon {...iconProps} />
        }
      }
      return <DashboardIcon {...iconProps} />
    })
    .with({ type: 'rich-text' }, () => <InputIcon {...iconProps} />)
    .with({ type: 'code-snippet' }, () => <CodeIcon {...iconProps} />)
    .with({ type: 'root' }, () => <></>)
    .with({ type: 'comment' }, () => <></>)
    .with({ type: 'reference' }, () => <UnionBlockIcon {...iconProps} />)
    .with({ type: 'select' }, () => <TextAlignJustifyIcon {...iconProps} />)
    .with({ type: 'color' }, () => <ColorWheelIcon {...iconProps} />)
    .with({ type: 'og-image' }, () => <OgImageIcon {...iconProps} />)
    .with({ type: 'icon' }, () => <SVGIcon {...iconProps} />)
    .with({ type: 'variant-set' }, () => <LayersIcon {...iconProps} />)
    .with({ type: 'event' }, () => <DiscIcon {...iconProps} />)
    .with({ type: 'workflow' }, () => <WorkflowBlockIcon {...iconProps} />)
    .with({ type: 'agent' }, () => (
      <STARTIcon height={15} width={15} {...iconProps} />
    ))
    .otherwise(() => <ComponentNoneIcon {...iconProps} />)
}

const DocumentIcon = ({
  forceNonEmptyDocumentIcon,
  iconProps,
  block,
}: Pick<
  BlockIconProps,
  'forceNonEmptyDocumentIcon' | 'iconProps' | 'block'
>) => {
  const safelyGetChildrenKeys = useBlockIconStore(
    (s) => s.safelyGetChildrenKeys
  )

  const { data: hasChildren } = useQuery(
    ['document-icon', block.id],
    () => {
      if (!block.id) return false
      const childrenKeys = safelyGetChildrenKeys(
        block.sourceBlockId ?? block.id
      )
      return childrenKeys.length > 0
    },
    {
      refetchInterval: 1000,
    }
  )

  const icon = useMemo(
    () =>
      forceNonEmptyDocumentIcon || hasChildren ? (
        <FileTextIcon {...iconProps} />
      ) : (
        <FileIcon {...iconProps} />
      ),
    [forceNonEmptyDocumentIcon, hasChildren, iconProps]
  )

  return icon
}

const ReferenceableIcon = ({
  block,
  iconProps,
  parent,
  forceReferenceLinkIcon,
  children,
}: Omit<BlockIconProps, 'forceNonEmptyDocumentIcon'> & {
  children: ReactNode
}) => {
  const parentChildrenKeys = useBlockIconStore(
    (s) => parent && s.safelyGetChildrenKeys(parent?.sourceBlockId ?? parent.id)
  )

  if (parent && isReferenceBlock(parent)) {
    forceReferenceLinkIcon = true
    if (
      parentChildrenKeys?.some(
        (cId) => cId === block.id || cId === block.sourceBlockId
      )
    ) {
      forceReferenceLinkIcon = false
    }
  }

  if (forceReferenceLinkIcon) {
    return <Link2Icon {...iconProps} />
  }
  return children
}
